/**
 * Deterministic rule hook execution for the Legendary Arena game engine.
 *
 * executeRuleHooks collects RuleEffect[] from registered hooks in
 * deterministic order without modifying G. The ImplementationMap pattern
 * maps data-only HookDefinition entries to handler functions at runtime.
 *
 * No boardgame.io imports. No G mutation. No .sort() (delegated to
 * getHooksForTrigger from WP-009A).
 */
import { getHooksForTrigger } from './ruleHooks.registry.js';
// why: executeRuleHooks returns effects without applying them. This lets
// tests assert what would happen without modifying G. It also allows callers
// to inspect, filter, or replay the effect list before application.
/**
 * Collects RuleEffect[] from all hooks registered for the given trigger.
 *
 * Hooks are retrieved in deterministic order via getHooksForTrigger (sorted
 * by priority ascending, then by id lexically). For each hook, the
 * corresponding handler is looked up in the implementationMap by hook id.
 * If no handler exists, a warning is logged and the hook is skipped.
 *
 * This function NEVER modifies G. It only reads G and the registry.
 *
 * @param gameState - Current game state (read-only access).
 * @param ctx - boardgame.io context (passed through to handlers).
 * @param triggerName - The trigger that fired.
 * @param payload - Trigger-specific payload data.
 * @param registry - The hook registry to query.
 * @param implementationMap - Handler functions keyed by hook id.
 * @returns Flat array of RuleEffect descriptions in hook execution order.
 */
export function executeRuleHooks(gameState, ctx, triggerName, payload, registry, implementationMap) {
    const sortedHooks = getHooksForTrigger(registry, triggerName);
    const allEffects = [];
    for (const hookDefinition of sortedHooks) {
        const handler = implementationMap[hookDefinition.id];
        if (!handler) {
            continue;
        }
        const effects = handler(gameState, ctx, payload, implementationMap);
        for (const effect of effects) {
            allEffects.push(effect);
        }
    }
    return allEffects;
}
//# sourceMappingURL=ruleRuntime.execute.js.map